// src/polyfill.ts
if (!Array.prototype.findLastIndex)
  Array.prototype.findLastIndex = function(predicate) {
    for (let i = this.length - 1; i >= 0; i--)
      if (predicate(this[i], i, this))
        return i;
    return -1;
  };
if (!Array.prototype.findLast)
  Array.prototype.findLast = function(predicate) {
    const i = this.findLastIndex(predicate);
    if (i !== -1)
      return this[i];
  };

// src/overlayApi.tsx
import {
  createMemo,
  ErrorBoundary,
  For,
  useContext as useContext4,
  on,
  createComputed,
  untrack
} from "solid-js";

// src/contexts/OverlayInstanceContext.ts
import { createContext, useContext } from "solid-js";
var OverlayInstanceContext = createContext();
var OverlayInstanceContext_default = OverlayInstanceContext;
var useOverlayComponent = () => {
  const context = useContext(OverlayInstanceContext);
  if (!context)
    throw new Error(
      "Attempted to call useOverlayComponent outside of OverlayInstanceContext."
    );
  const { close, openSelf, index } = context(null);
  return {
    close,
    openSelf,
    index
  };
};

// src/overlayApi.tsx
import {
  createArray,
  createDeepObject,
  createObject,
  createBoundSignal
} from "solid-signals";

// src/contexts/OverlayLayoutContext.ts
import { createContext as createContext2, useContext as useContext2 } from "solid-js";
var OverlayLayoutContext = createContext2(
  (Component2) => {
    return {
      close() {
        throw new Error(
          "overlays: Attempted to remove self outside of OverlayLayoutContext.Provider"
        );
      },
      withBackdropProps(props) {
        throw new Error(
          "overlays: Attempted to set backdrop props outside of OverlayLayoutContext.Provider"
        );
      },
      isPresent: () => true,
      safeToRemove() {
        throw new Error(
          'overlays: "safeToRemove" called outside of OverlayLayoutContext.Provider'
        );
      },
      getRelative(delta) {
        throw new Error(
          "overlays: Attempted to get relative overlay outside of OverlayLayoutContext.Provider"
        );
      },
      id: -1,
      index: () => -1,
      isCurrent: () => false,
      root: {
        current: () => ({
          index: -1,
          id: -1
        })
      }
    };
  }
);
var OverlayLayoutContext_default = OverlayLayoutContext;
var useOverlayLayout = (Layout) => {
  return useContext2(OverlayLayoutContext)(Layout);
};

// src/contexts/OverlayBackdropContext.ts
import { createContext as createContext3, useContext as useContext3 } from "solid-js";
var OverlayBackdropContext = createContext3({
  show: () => false,
  closeCurrent() {
    throw new Error(
      'overlays: "closeCurrent" called outside of OverlayBackdropContext.Provider'
    );
  }
});
var OverlayBackdropContext_default = OverlayBackdropContext;
var useOverlayBackdrop = () => useContext3(OverlayBackdropContext);

// src/overlayApi.tsx
import { reactiveProps, signalValuePromise } from "solid-u";
import { Tuple } from "record-tuple";

// src/contexts/OverlaysContext.ts
import { createContext as createContext4 } from "solid-js";
var OverlaysContext = createContext4();
var OverlaysContext_default = OverlaysContext;

// src/overlayApi.tsx
var overlayApi = (overlays, { DefaultLayout } = {}) => {
  const [componentByKey, setComponentByKey] = createObject({});
  const OverlaysContextRef = OverlaysContext_default;
  const InstanceContextRef = OverlayInstanceContext_default;
  return {
    create({
      hooks = {},
      defaultConfig = {}
    }) {
      const OverlaysContext2 = OverlaysContextRef;
      const OverlayInstanceContext2 = InstanceContextRef;
      const useOverlaysController = () => {
        const context = useContext4(OverlaysContext2);
        if (!context)
          throw new Error(
            "Attempted to call useOverlaysController outside of OverlaysContext."
          );
        const { open, closeAll } = context;
        return { open, closeAll };
      };
      const useOverlaysBase = () => {
        const context = useContext4(OverlaysContext2);
        if (!context)
          throw new Error(
            "Attempted to call useOverlaysBase outside of OverlaysContext."
          );
        const { render, stack, closeCurrent } = context;
        return { render, stack, closeCurrent };
      };
      const useOverlay = (Component2) => {
        const context = useContext4(OverlayInstanceContext2);
        if (!context)
          throw new Error(
            "Attempted to call useOverlay outside of OverlayInstanceContext."
          );
        const { close, updateOwnProps, openSelf, withLayoutProps, onClose } = context(Component2);
        const { withBackdropProps } = useContext4(OverlayLayoutContext_default)(
          Component2.Layout || DefaultLayout
        );
        return {
          onClose,
          close,
          updateOwnProps,
          openSelf,
          withLayoutProps,
          withBackdropProps
        };
      };
      const getComponentConfig = (Component2) => ({
        duplicateBehavior: "allow",
        limit: "none",
        validateRenderContext: () => true,
        ...defaultConfig,
        ...Component2.config
      });
      const IdentityLayout = Object.assign(
        (props) => <>{props.children}</>,
        {
          Backdrop: (props) => <>{props.children}</>
        }
      );
      const getLayout = (Component2) => Component2?.Layout || DefaultLayout || IdentityLayout;
      function OverlaysProvider(_props) {
        const { data, onChange, children } = reactiveProps(_props);
        const [stack, setStack] = createBoundSignal.wrap(
          createArray([]),
          [data, onChange?.()]
        );
        const entryById = createMemo(
          () => Object.fromEntries(stack().map((entry) => [entry[1], entry]))
        );
        const [stateById, setStateById] = createDeepObject.wrap(
          createObject({})
        );
        const reconcileStates = createMemo(() => {
          if (Tuple(...Object.keys(untrack(stateById))) === Tuple(...stack().map(([, id]) => `${id}`)))
            return;
          setStateById(
            (prev) => Object.fromEntries(
              stack().map(([, id]) => [
                id,
                prev[id] || {
                  isPresent: true,
                  closeListeners: [],
                  onClose() {
                  },
                  backdropProps: () => null,
                  layoutProps: () => null
                }
              ])
            )
          );
        });
        const current = createMemo(() => {
          const index = stack().findLastIndex(
            ([, id2]) => stateById()[id2]?.isPresent
          );
          if (index === -1)
            return { index: -1, id: -1, key: "" };
          const [key, id] = stack()[index];
          return { index, id, key };
        });
        const remove = (id, result) => {
          const state = stateById()[id];
          if (!state?.isPresent)
            return;
          setStateById.deep[id].isPresent(false);
          state.onClose(
            result ?? componentByKey()[stack().find(([, _id]) => id === _id)[0]].defaultResult
          );
        };
        const findById = (id) => ([, _id]) => _id === id;
        const renderOverlays = (renderContext) => {
          const backdrops = createMemo(
            () => {
              const map = /* @__PURE__ */ new Map();
              stack().forEach(([key, id], i) => {
                const state = stateById()[id];
                if (!state)
                  return;
                const Component2 = createMemo(() => componentByKey()[key])();
                if (!Component2 || Component2 === "pending")
                  return;
                const { validateRenderContext } = getComponentConfig(Component2);
                if (!validateRenderContext(renderContext))
                  return;
                const { Backdrop } = getLayout(Component2);
                if (!Backdrop)
                  return;
                if (!map.has(Backdrop) || i <= current().index) {
                  map.set(Backdrop, state.backdropProps);
                }
              });
              return map;
            },
            null,
            {
              equals(prev, next) {
                if (prev.size !== next.size)
                  return false;
                for (const [Backdrop, props] of prev) {
                  if (next.get(Backdrop) !== props)
                    return false;
                }
                return true;
              }
            }
          );
          return <ErrorBoundary
            fallback={(err, reset) => {
              on(stack, reset);
              setStack([]);
              return <></>;
            }}
          >
            <For each={[...backdrops().keys()]}>{(Backdrop) => <OverlayBackdropContext_default.Provider
              value={{
                show: createMemo(
                  () => current().id !== -1 && ((Component2 = createMemo(
                    () => componentByKey()[current().key]
                  )()) => Component2 && (Component2 === "pending" || getLayout(Component2).Backdrop === Backdrop))()
                ),
                closeCurrent() {
                  const [, overlayId] = stack().findLast(
                    ([, id]) => stateById()[id].isPresent
                  ) || [];
                  if (overlayId == null)
                    return;
                  remove(overlayId);
                }
              }}
            ><Backdrop {...backdrops().get(Backdrop)} /></OverlayBackdropContext_default.Provider>}</For>
            <For
              each={
                // The body of this For depends on stateById,
                // `reconcileStates()` ensures its memo body executes before
                // this
                (reconcileStates(), stack().map(([, id]) => id))
              }
            >{(id, index) => {
              const key = entryById()[id][0];
              const props = () => entryById()[id][2];
              return <>{(() => {
                const Component2 = createMemo(
                  () => componentByKey()[key]
                )();
                if (!Component2) {
                  (async () => {
                    setComponentByKey.update({
                      [key]: "pending"
                    });
                    setComponentByKey.update({
                      [key]: (await overlays[key]()).default
                    });
                  })();
                  return null;
                }
                if (Component2 === "pending")
                  return null;
                const { validateRenderContext } = getComponentConfig(Component2);
                if (!validateRenderContext(renderContext?.()))
                  return null;
                const Layout = getLayout(Component2);
                const state = () => stateById()[id];
                const isPresent = createMemo(() => state().isPresent);
                return <OverlayInstanceContext2.Provider
                  value={() => ({
                    index,
                    onClose(handler) {
                      setStateById.deep[id].closeListeners([
                        ...stateById()[id]?.closeListeners || [],
                        handler
                      ]);
                    },
                    close(result) {
                      remove(id, result);
                    },
                    updateOwnProps(newProps) {
                      setStack.find(findById(id), [
                        key,
                        id,
                        { ...props(), ...newProps }
                      ]);
                    },
                    openSelf: Object.assign(
                      (newProps, context) => overlaysController.open(
                        key,
                        { ...props(), ...newProps },
                        context
                      ),
                      {
                        keyOnly: (props2, context) => overlaysController.open(
                          key,
                          props2,
                          context
                        )
                      }
                    ),
                    withLayoutProps(props2) {
                      createComputed(() => {
                        setStateById.deep[id]?.layoutProps(props2());
                      });
                    }
                  })}
                ><OverlayLayoutContext_default.Provider
                  value={() => ({
                    close() {
                      remove(id);
                    },
                    withBackdropProps(props2) {
                      createComputed(() => {
                        setStateById.deep[id].backdropProps(
                          props2()
                        );
                      });
                    },
                    isPresent,
                    safeToRemove() {
                      if (isPresent())
                        throw new Error(
                          '"safeToRemove" called while component is still present'
                        );
                      if (!(id in entryById()))
                        throw new Error(
                          `Already called "safeToRemove" in ${key}`
                        );
                      setStack(
                        stack().filter(
                          ([, entryId]) => entryId !== id
                        )
                      );
                    },
                    id,
                    index,
                    isCurrent: () => current().id === id,
                    getRelative: (delta) => createMemo(() => {
                      const i = stack().findIndex(findById(id)) + delta;
                      const entry = stack()[i];
                      if (!entry)
                        return null;
                      const [key2, , props2] = entry;
                      return {
                        key: key2,
                        id,
                        props: props2,
                        // Prevent stack().findIndex from running on unrelated updates
                        ...createMemo(() => stateById()[id])(),
                        Component: componentByKey()[key2]
                      };
                    })(),
                    root: { current }
                  })}
                ><Layout {...state()?.layoutProps}><Component2 {...props()} /></Layout></OverlayLayoutContext_default.Provider></OverlayInstanceContext2.Provider>;
              })()}</>;
            }}</For>
          </ErrorBoundary>;
        };
        const overlaysController = {
          open(key, props = {}, context) {
            hooks.open?.(key, props, context);
            const componentLoad = signalValuePromise(
              () => componentByKey()[key],
              (component) => component && component !== "pending"
            );
            let resolveResult = null;
            const result = new Promise((resolve) => {
              resolveResult = resolve;
            });
            (async () => {
              const Component2 = componentByKey()[key];
              if (Component2) {
                const { limit } = getComponentConfig(
                  Component2 === "pending" ? await componentLoad : Component2
                );
                if (limit === "once-per-session")
                  return resolveResult(null);
              }
              const maxId = Math.max(0, ...stack().map(([, id2]) => id2));
              const id = maxId + 1;
              setStack.push([key, id, props]);
              setStateById.deep[id].onClose(() => (result2) => {
                for (const listener of stateById()[id].closeListeners) {
                  listener(result2);
                }
                resolveResult(result2);
              });
            })();
            return {
              result,
              componentLoad: componentLoad.then(() => {
              })
            };
          },
          closeAll() {
            for (const id of Object.keys(stateById())) {
              remove(id);
            }
          },
          closeCurrent() {
            remove(current().id);
          },
          current,
          render: renderOverlays,
          stack
        };
        return <OverlaysContext2.Provider value={overlaysController}>{(() => {
          const childrenOrFn = children();
          return typeof childrenOrFn === "function" && childrenOrFn.length > 0 ? childrenOrFn({ renderOverlays }) : childrenOrFn;
        })()}</OverlaysContext2.Provider>;
      }
      return {
        OverlaysContext: OverlaysContext2,
        OverlaysProvider,
        useOverlaysController,
        useOverlaysBase,
        useOverlay,
        useOverlayLayout,
        useOverlayBackdrop
      };
    }
  };
};
var overlayApi_default = overlayApi;

// src/layouts/ModalLayout.tsx
import { createEffect, createSignal, onMount } from "solid-js";
import { reactiveProps as reactiveProps3 } from "solid-u";

// src/backdrops/DarkenBackdrop.tsx
import { reactiveProps as reactiveProps2 } from "solid-u";
function DarkenBackdrop(_props) {
  const { noClose } = reactiveProps2(_props);
  const { show, closeCurrent } = useOverlayBackdrop();
  return <div
    class={["solid-overlays:darken-backdrop", show() && "show"].filter(Boolean).join(" ")}
    onClick={() => {
      if (noClose?.() || !show())
        return;
      closeCurrent();
    }}
  />;
}
var DarkenBackdrop_default = DarkenBackdrop;

// src/layouts/ModalLayout.tsx
function ModalLayout(_props) {
  const { children } = reactiveProps3(_props);
  const [initial, setInitial] = createSignal(true);
  onMount(() => {
    requestAnimationFrame(() => {
      setInitial(false);
    });
  });
  const layout = useOverlayLayout(ModalLayout);
  const prev = () => layout.getRelative(-1);
  const left = () => layout.index() < layout.root.current().index;
  const right = () => layout.index() > layout.root.current().index;
  const enterRight = () => layout.isCurrent() && prev() && initial() && layout.isPresent();
  const cleared = () => layout.root.current().index === -1;
  createEffect(() => {
    if (!layout.isPresent())
      setTimeout(() => {
        layout.safeToRemove();
      }, 400);
  });
  return <div
    class={[
      "solid-overlays:modal",
      left() && "left",
      (right() || enterRight()) && !cleared() && "right",
      layout.isCurrent() && "show"
    ].filter(Boolean).join(" ")}
  ><div><div>{children()}</div></div></div>;
}
ModalLayout.Backdrop = DarkenBackdrop_default;
var ModalLayout_default = ModalLayout;
export {
  DarkenBackdrop_default as DarkenBackdrop,
  ModalLayout_default as ModalLayout,
  overlayApi_default as default,
  useOverlayBackdrop,
  useOverlayComponent,
  useOverlayLayout
};
